import axios from 'axios'
import { ACCOUNT_LOCKED_REASON } from 'lib/passwordPolicy'

/**
 * Web session: 30 min access token + 30 min refresh token (BE rotates it on
 * every refresh and slides its expiry, see cpac-isupply-api WebRefreshToken).
 *
 * The access token lives in localStorage['access-token'] (read directly by
 * ~300 call sites) and in redux state.auth.accessToken. A refresh therefore
 * updates both and never rebuilds `profile` from the new token — only its
 * exp/tokenExp change.
 *
 * Sessions without a refresh token (logged in before this shipped) behave as
 * before: the Header popup fires when the access token expires.
 */

export const ACCESS_TOKEN_KEY = 'access-token'
export const PROFILE_KEY = 'profile'
export const REFRESH_TOKEN_KEY = 'refresh-token'
export const REFRESH_AHEAD_SECONDS = 120
// opt-in header: only the FE that can refresh gets 30 min tokens (BE WebSession::OPT_IN_HEADER)
export const WEB_SESSION_HEADERS = { 'X-Web-Session': '1' }
// never let a stalled BE or a held lock hang the app (Layout renders nothing while restoring)
export const REQUEST_TIMEOUT_MS = 10000
export const REFRESH_TIMEOUT_MS = 15000

const LOCK_NAME = 'isupply-web-refresh'
const LOCK_KEY = 'refresh-token-lock'
const LOCK_STALE_MS = 15000
const LOCK_WAIT_MS = 300

const endpoint = () => process.env.REACT_APP_LARAVEL_API_ENDPOINT

export const REFRESH_URL_PATH = '/auth/web-refresh'
export const SESSION_URL_PATH = '/auth/web-session'
export const LOGOUT_URL_PATH = '/auth/web-logout'

// account locked while signed in (user 20/10/2026 · DECISIONS #27): the BE refuses
// the refresh with 401 + error account_locked → leave for the account-locked view
export const ACCOUNT_LOCKED_PATH = '/logincpac'
export const ACCOUNT_LOCKED_NOTICE_KEY = 'account-locked-at-refresh'

let onRefreshed = null
let onAccountLocked = null
let accountLockedHandled = false
let inflight = null

export function setOnRefreshed(fn) {
  onRefreshed = fn
}

export function setOnAccountLocked(fn) {
  onAccountLocked = fn
}

/** A refresh refused because the account is locked (strict: 401 + error account_locked). */
export function isAccountLockedRefreshError(e) {
  return Boolean(
    e && e.response && e.response.status === 401 && e.response.data && e.response.data.error === ACCOUNT_LOCKED_REASON,
  )
}

const safeGet = (key) => {
  try {
    return localStorage.getItem(key)
  } catch (e) {
    return null
  }
}

export function getRefreshToken() {
  return safeGet(REFRESH_TOKEN_KEY)
}

export function setRefreshToken(token) {
  if (token) localStorage.setItem(REFRESH_TOKEN_KEY, token)
  else localStorage.removeItem(REFRESH_TOKEN_KEY)
}

export function clearRefreshToken() {
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

export function decodeTokenExp(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(window.atob(base64))
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch (e) {
    return null
  }
}

export function nowSeconds() {
  return Math.floor(Date.now() / 1000)
}

export function isNearExpiry(exp, now = nowSeconds(), ahead = REFRESH_AHEAD_SECONDS) {
  if (typeof exp !== 'number') return true
  return exp - now <= ahead
}

/**
 * Store a refreshed token without touching the rest of the profile.
 */
export function applyRefreshedToken(token, refreshToken) {
  const exp = decodeTokenExp(token)
  localStorage.setItem(ACCESS_TOKEN_KEY, token)
  let profile = {}
  try {
    profile = JSON.parse(safeGet(PROFILE_KEY) || '{}') || {}
  } catch (e) {
    profile = {}
  }
  profile.exp = exp
  profile.tokenExp = exp
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile))
  if (refreshToken) setRefreshToken(refreshToken)
  if (onRefreshed) onRefreshed(token, exp)
  return exp
}

/**
 * On app start: the stored access token is (nearly) expired but a refresh
 * token exists — refresh before loading credentials.
 */
export function shouldRestoreSession() {
  const stored = safeGet(ACCESS_TOKEN_KEY)
  return Boolean(stored && getRefreshToken() && isNearExpiry(decodeTokenExp(stored)))
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

export function withTimeout(promise, ms) {
  let timer
  const timeout = new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(new Error('web session timeout')), ms)
  })
  return Promise.race([promise, timeout]).then(
    (v) => {
      clearTimeout(timer)
      return v
    },
    (e) => {
      clearTimeout(timer)
      throw e
    },
  )
}

/**
 * Run fn while holding a lock shared by every tab, so only one tab rotates
 * the refresh token at a time.
 */
export async function withCrossTabLock(fn) {
  if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
    return navigator.locks.request(LOCK_NAME, fn)
  }
  const deadline = Date.now() + LOCK_STALE_MS
  for (;;) {
    const held = Number(safeGet(LOCK_KEY) || 0)
    if (!held || Date.now() - held > LOCK_STALE_MS || Date.now() > deadline) break
    await sleep(LOCK_WAIT_MS)
  }
  localStorage.setItem(LOCK_KEY, String(Date.now()))
  try {
    return await fn()
  } finally {
    localStorage.removeItem(LOCK_KEY)
  }
}

/**
 * Get a fresh access token. Single-flight per tab and locked across tabs:
 * if another tab refreshed meanwhile its token is adopted instead of
 * rotating again. Resolves the access token; rejects when the session is
 * over (no/invalid refresh token) — the refresh token is then cleared.
 */
export function refreshSession({ force = false, post = axios.post } = {}) {
  if (inflight) return inflight

  inflight = withTimeout(withCrossTabLock(async () => {
    const current = safeGet(ACCESS_TOKEN_KEY)
    if (!force && current && !isNearExpiry(decodeTokenExp(current))) {
      if (onRefreshed) onRefreshed(current, decodeTokenExp(current))
      return current
    }

    const refreshToken = getRefreshToken()
    if (!refreshToken) throw new Error('no refresh token')

    try {
      const res = await post(`${endpoint()}${REFRESH_URL_PATH}`, { refresh_token: refreshToken }, { timeout: REQUEST_TIMEOUT_MS })
      applyRefreshedToken(res.data.token, res.data.refresh_token)
      return res.data.token
    } catch (e) {
      // another tab may have rotated first and released the lock
      const latest = safeGet(ACCESS_TOKEN_KEY)
      if (latest && latest !== current && !isNearExpiry(decodeTokenExp(latest))) {
        if (onRefreshed) onRefreshed(latest, decodeTokenExp(latest))
        return latest
      }
      if (e && e.response && e.response.status === 401) clearRefreshToken()
      // every caller (keeper timer, axios interceptors, page-load restore) goes through
      // here, and the first 401 already cleared the refresh token — decide it now, once
      if (isAccountLockedRefreshError(e)) handleAccountLocked()
      throw e
    }
  }), REFRESH_TIMEOUT_MS)

  const done = () => {
    inflight = null
  }
  inflight.then(done, done)
  return inflight
}

function handleAccountLocked() {
  if (accountLockedHandled) return
  accountLockedHandled = true
  ;(onAccountLocked || goToAccountLockedPage)()
}

/**
 * Default account-locked handling: forget the session, leave a one-shot notice
 * for the login page (sessionStorage: survives the reload, not the tab) and
 * reload on /logincpac — Layout's BrowserRouter keeps its own history, so a
 * history.push would not move it.
 */
export function goToAccountLockedPage({ location = window.location, storage = window.sessionStorage } = {}) {
  let username = ''
  try {
    username = (JSON.parse(safeGet(PROFILE_KEY) || '{}') || {}).username || ''
  } catch (e) {
    username = ''
  }
  try {
    storage.setItem(ACCOUNT_LOCKED_NOTICE_KEY, JSON.stringify({ username }))
  } catch (e) {
    // no sessionStorage: the login page shows the plain form
  }
  localStorage.removeItem(ACCESS_TOKEN_KEY)
  localStorage.removeItem(PROFILE_KEY)
  clearRefreshToken()
  location.replace(ACCOUNT_LOCKED_PATH)
}

/**
 * Login page: read (and clear) the notice goToAccountLockedPage left.
 * Returns { username } or null.
 */
export function takeAccountLockedNotice(storage = window.sessionStorage) {
  try {
    const raw = storage.getItem(ACCOUNT_LOCKED_NOTICE_KEY)
    if (raw === null) return null
    storage.removeItem(ACCOUNT_LOCKED_NOTICE_KEY)
    const notice = JSON.parse(raw) || {}
    return { username: typeof notice.username === 'string' ? notice.username : '' }
  } catch (e) {
    return null
  }
}

/**
 * After a login: keep the refresh token the login response carried, or
 * trade the fresh access token for a web session (login paths that do not
 * pass it through, e.g. the OAuth service). Resolves the access token to
 * store — the exchanged 30 min one, or the login's own token when there is
 * no web session (old behavior). Never throws.
 */
export async function establishSession(accessToken, refreshToken, { post = axios.post } = {}) {
  if (refreshToken) {
    setRefreshToken(refreshToken)
    return accessToken
  }
  clearRefreshToken()
  try {
    const res = await post(`${endpoint()}${SESSION_URL_PATH}`, null, {
      headers: { Authorization: `Bearer ${accessToken}` },
      timeout: REQUEST_TIMEOUT_MS,
    })
    const data = (res && res.data) || {}
    if (data.refresh_token && data.token) {
      setRefreshToken(data.refresh_token)
      return data.token
    }
  } catch (e) {
    // no web session — keep the login token
  }
  return accessToken
}

/**
 * Revoke the refresh token on the server and forget it. Fire and forget.
 */
export function endSession({ post = axios.post } = {}) {
  const refreshToken = getRefreshToken()
  clearRefreshToken()
  if (!refreshToken) return Promise.resolve()
  return post(`${endpoint()}${LOGOUT_URL_PATH}`, { refresh_token: refreshToken }).catch(() => {})
}

const isSessionUrl = (url = '') =>
  url.indexOf(REFRESH_URL_PATH) !== -1 || url.indexOf(SESSION_URL_PATH) !== -1 || url.indexOf(LOGOUT_URL_PATH) !== -1

const bearerOf = (config) => {
  const headers = (config && config.headers) || {}
  const value = headers.Authorization || headers.authorization
  return typeof value === 'string' && value.indexOf('Bearer ') === 0 ? value.slice(7) : null
}

const setBearer = (config, token) => {
  if (config.headers.authorization !== undefined && config.headers.Authorization === undefined) {
    config.headers.authorization = `Bearer ${token}`
  } else {
    config.headers.Authorization = `Bearer ${token}`
  }
}

/**
 * axios interceptors on the default instance:
 *  - request: token about to expire → refresh first, then swap the header
 *    when it still carries the old token
 *  - response: 401 with a refresh token → refresh once and retry, before the
 *    caller's handler (lib/fetch.js clears localStorage on 401)
 * Native fetch() callers are not covered; the Header timer keeps
 * localStorage fresh for them.
 */
export function installAxiosInterceptors(instance = axios) {
  const requestId = instance.interceptors.request.use(async (config) => {
    const sent = bearerOf(config)
    if (!sent || isSessionUrl(config.url) || !getRefreshToken()) return config
    if (!isNearExpiry(decodeTokenExp(sent))) return config
    try {
      const fresh = await refreshSession()
      if (fresh && fresh !== sent) setBearer(config, fresh)
    } catch (e) {
      // session over — send as is; the 401 path handles it
    }
    return config
  })

  const responseId = instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const config = error && error.config
      const status = error && error.response && error.response.status
      if (status !== 401 || !config || config.__webSessionRetried || isSessionUrl(config.url)) throw error
      const sent = bearerOf(config)
      if (!sent || !getRefreshToken()) throw error
      try {
        const fresh = await refreshSession({ force: true })
        config.__webSessionRetried = true
        setBearer(config, fresh)
        return instance(config)
      } catch (e) {
        throw error
      }
    },
  )

  return () => {
    instance.interceptors.request.eject(requestId)
    instance.interceptors.response.eject(responseId)
  }
}

/**
 * Keep redux in sync when another tab refreshes.
 */
export function installStorageSync(target = window) {
  const handler = (e) => {
    if (e.key === ACCESS_TOKEN_KEY && e.newValue && onRefreshed) {
      onRefreshed(e.newValue, decodeTokenExp(e.newValue))
    }
  }
  target.addEventListener('storage', handler)
  return () => target.removeEventListener('storage', handler)
}

// test hook
export function __resetForTests() {
  onRefreshed = null
  onAccountLocked = null
  accountLockedHandled = false
  inflight = null
}
